export const WIDTH = 390;
export const HEIGHT = 844;
export const GRAVITY = 900;
export const JUMP = 530;
export const SPEED = 245;
export type Platform = {
  id: number;
  x: number;
  y: number;
  width: number;
  baseX: number;
  kind: "moss" | "moving" | "spring";
  phase: number;
};
export type World = {
  x: number;
  y: number;
  vy: number;
  camera: number;
  peak: number;
  time: number;
  level: number;
  platforms: Platform[];
  checkpoint: number;
  respawn: number;
  falls: number;
};
// Every gap is below the normal jump apex (156px). Even the largest horizontal
// separation is reachable at SPEED before descending to the next ledge.
export function platformAt(level: number): Platform {
  const positions = [139, 249, 61, 228, 88, 246, 75, 213];
  const width = level < 10 ? [110, 124, 92, 118][level % 4] : 88;
  const x = positions[level % positions.length];
  return {
    id: level,
    x,
    baseX: x,
    y: 742 - level * 82,
    width,
    kind:
      level > 3 && level % 7 === 4
        ? "moving"
        : level > 2 && level % 9 === 3
          ? "spring"
          : "moss",
    phase: level * 1.4,
  };
}
export function createWorld(): World {
  return {
    x: 189,
    y: 742,
    vy: -JUMP,
    camera: 0,
    peak: 742,
    time: 0,
    level: 12,
    platforms: Array.from({ length: 12 }, (_, i) => platformAt(i)),
    checkpoint: 0,
    respawn: 0,
    falls: 0,
  };
}
export function step(
  world: World,
  direction: number,
  dt: number,
): "hop" | "spring" | "fall" | "respawn" | undefined {
  world.time += dt;
  if (world.respawn > 0) {
    world.respawn = Math.max(0, world.respawn - dt);
    if (world.respawn > 1e-8) return;
    world.respawn = 0;
    const safe = platformAt(world.checkpoint);
    if (safe.kind === "moving") safe.x = movingX(safe, world.time);
    world.x = safe.x + safe.width / 2;
    world.y = safe.y;
    world.vy = -JUMP;
    world.camera = Math.min(0, safe.y - HEIGHT * 0.7);
    const first = Math.max(0, world.checkpoint - 8);
    world.platforms = Array.from({ length: 23 }, (_, i) =>
      platformAt(first + i),
    );
    world.level = first + 23;
    return "respawn";
  }
  const previousY = world.y;
  world.x = Math.max(
    15,
    Math.min(WIDTH - 15, world.x + direction * SPEED * dt),
  );
  world.vy += GRAVITY * dt;
  world.y += world.vy * dt;
  let landing: "hop" | "spring" | undefined;
  for (const p of world.platforms) {
    if (p.kind === "moving") p.x = movingX(p, world.time);
    if (
      world.vy > 0 &&
      previousY <= p.y &&
      world.y >= p.y &&
      world.x + 12 > p.x &&
      world.x - 12 < p.x + p.width
    ) {
      world.checkpoint = p.id;
      world.y = p.y;
      world.vy = p.kind === "spring" ? -710 : -JUMP;
      landing = p.kind === "spring" ? "spring" : "hop";
      break;
    }
  }
  world.peak = Math.min(world.peak, world.y);
  world.camera = Math.min(world.camera, world.y - HEIGHT * 0.55);
  while (742 - world.level * 82 > world.camera - 250)
    world.platforms.push(platformAt(world.level++));
  world.platforms = world.platforms.filter(
    (p) => p.y < world.camera + HEIGHT + 100,
  );
  if (world.y - world.camera > HEIGHT + 65) {
    world.respawn = 5;
    world.falls++;
    world.vy = 0;
    return "fall";
  }
  return landing;
}
export const altitude = (world: World) =>
  Math.max(0, Math.floor((742 - world.peak) / 8));

function movingX(p: Platform, time: number) {
  return Math.max(
    15,
    Math.min(
      WIDTH - p.width - 15,
      p.baseX + Math.sin(time * 1.2 + p.phase) * 25,
    ),
  );
}
